import { normalizarHabilidadeId } from '../../lib/skillAliases.js';
import { formatDate, habilidadeLabels } from '../../lib/utils.js';

const RESULT_LABELS = {
  conseguiu: 'Conseguiu',
  parcial: 'Parcial',
  nao_conseguiu: 'Ainda não',
  'não conseguiu': 'Ainda não'
};

const SUPPORT_LABELS = {
  independente: 'Independente',
  pista_leve: 'Pista leve',
  ajuda_moderada: 'Ajuda moderada',
  ajuda_intensa: 'Ajuda intensa'
};

function asList(value) {
  if (!value) return [];
  return Array.isArray(value) ? value.filter(Boolean) : [value];
}

export function goalSkillIds(goal = {}) {
  return asList(goal.habilidades_alvo || goal.habilidades || goal.habilidade_alvo || goal.habilidade)
    .map(normalizarHabilidadeId)
    .filter(Boolean);
}

function logSkillIds(log = {}) {
  return [
    ...asList(log.habilidades || log.habilidades_alvo || log.habilidade),
    ...(log.itens || []).flatMap((item) => asList(item.habilidades || item.habilidadesAlvo))
  ].map(normalizarHabilidadeId).filter(Boolean);
}

function itemSkillIds(item = {}) {
  return asList(item.habilidades || item.habilidadesAlvo).map(normalizarHabilidadeId).filter(Boolean);
}

function scoreFromItem(item = {}) {
  if (item.score === null || item.score === undefined || item.score === '') {
    if (item.resultado === 'conseguiu') return 100;
    if (item.resultado === 'parcial') return 50;
    if (['nao_conseguiu', 'não conseguiu'].includes(item.resultado)) return 0;
    return null;
  }
  if (Number.isFinite(Number(item.score))) {
    const score = Number(item.score);
    return Math.round(score <= 1 ? score * 100 : score);
  }
  return null;
}

function resultSummary(item = {}) {
  const result = RESULT_LABELS[item.resultado] || '';
  const support = SUPPORT_LABELS[item.apoio] || '';
  const score = scoreFromItem(item);
  return [result, support, score === null ? '' : `${score}/100`].filter(Boolean).join(' · ') || 'Evidência registrada';
}

function daysBetween(from, to) {
  const start = new Date(`${from}T12:00:00`);
  const end = new Date(`${to}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return Math.floor((end - start) / 86400000);
}

export function barrierFromCaseStudy(caseStudy = {}) {
  return String(caseStudy?.blocks?.barriers?.summary || '').trim();
}

export function assessmentBaseline(assessment = {}, skillId = '') {
  const canonical = normalizarHabilidadeId(skillId);
  const scores = assessment?.pontuacoes || assessment?.habilidades || assessment?.scores || {};
  const entry = Object.entries(scores).find(([id]) => normalizarHabilidadeId(id) === canonical);
  const value = entry ? Number(entry[1]) : null;
  return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : null;
}

export function addDaysISO(date, days) {
  const base = new Date(`${date}T12:00:00`);
  if (Number.isNaN(base.getTime())) return '';
  base.setUTCDate(base.getUTCDate() + days);
  return base.toISOString().slice(0, 10);
}

export function buildGoalSuggestion({ skillId, barrier = '' } = {}) {
  const skill = habilidadeLabels[normalizarHabilidadeId(skillId)] || 'a habilidade selecionada';
  const context = barrier ? 'diante da barreira registrada' : 'em atividade funcional';
  return `Ampliar ${skill.toLowerCase()} ${context}, demonstrando a habilidade em 4 de 5 oportunidades com apoio progressivamente reduzido até a próxima revisão.`;
}

export function createLinkedGoal({ id, description, skillId, caseStudy = {}, assessment = {}, today, reviewDays = 90 } = {}) {
  const canonicalSkill = normalizarHabilidadeId(skillId);
  const barrier = barrierFromCaseStudy(caseStudy);
  const observedBaseline = assessmentBaseline(assessment, canonicalSkill);
  const baseline = observedBaseline ?? 0;
  const target = observedBaseline === null ? 70 : Math.min(100, Math.max(70, baseline + 20));
  return {
    id,
    descricao: String(description || '').trim(),
    habilidade_alvo: canonicalSkill,
    habilidades_alvo: [canonicalSkill],
    barreira: barrier,
    barreira_origem: barrier ? 'estudo_caso.barriers' : 'nao_informada',
    linha_base: baseline,
    linha_base_origem: observedBaseline === null ? 'nao_observada' : 'avaliacao_pedagogica',
    alvo_numerico: target,
    prazo: addDaysISO(today, reviewDays),
    revisao_origem: 'prazo_inicial_automatico',
    criadoEm: today,
    status: 'em_andamento'
  };
}

export function evidenceForGoal(goal = {}, logs = [], { today = new Date().toISOString().slice(0, 10), staleDays = 30, limit = 3 } = {}) {
  const targets = new Set(goalSkillIds(goal));
  const related = (logs || []).filter((log) => {
    if (asList(log.metaIds || log.goalIds).includes(goal.id)) return true;
    const skills = logSkillIds(log);
    return targets.size ? skills.some((skill) => targets.has(skill)) : false;
  }).sort((a, b) => String(b.data || b.date || '').localeCompare(String(a.data || a.date || '')));

  const items = related.flatMap((log) => {
    const date = log.data || log.date || '';
    const matchingItems = (log.itens || []).filter((item) => {
      if (!targets.size) return true;
      return itemSkillIds(item).some((skill) => targets.has(skill));
    });
    if (matchingItems.length) return matchingItems.map((item) => ({
      date,
      activity: item.titulo || item.atividadeTitulo || log.objetivo || 'Atendimento AEE',
      result: resultSummary(item),
      score: scoreFromItem(item),
      logId: log.id || ''
    }));
    return [{ date, activity: log.objetivo || 'Atendimento AEE', result: 'Atendimento relacionado à habilidade', score: null, logId: log.id || '' }];
  }).slice(0, limit);

  const lastDate = related[0]?.data || related[0]?.date || '';
  const daysWithoutEvidence = lastDate ? daysBetween(lastDate, today) : null;
  const alert = !related.length
    ? 'Nenhum atendimento relacionado a esta meta foi registrado ainda.'
    : Number.isFinite(daysWithoutEvidence) && daysWithoutEvidence > staleDays
      ? `${daysWithoutEvidence} dias desde a última evidência relacionada.`
      : '';
  return {
    items,
    activities: [...new Set(items.map((item) => item.activity))],
    relatedLogs: related.length,
    lastDate,
    daysWithoutEvidence,
    alert
  };
}

export function goalTraceability(goal = {}, { caseStudy = {}, logs = [], today } = {}) {
  const storedBarrier = String(goal.barreira || '').trim();
  const currentBarrier = barrierFromCaseStudy(caseStudy);
  return {
    barrier: storedBarrier || currentBarrier,
    barrierSource: storedBarrier
      ? (goal.barreira_origem === 'estudo_caso.barriers' ? 'Estudo de caso na criação da meta' : 'Registro da meta')
      : currentBarrier ? 'Estudo de caso atual' : 'Barreira ainda não registrada',
    skills: goalSkillIds(goal),
    evidence: evidenceForGoal(goal, logs, { ...(today ? { today } : {}) }),
    reviewDate: goal.prazo || ''
  };
}

export function goalReportText(goal = {}, context = {}) {
  const trace = goalTraceability(goal, context);
  const skill = trace.skills.map((id) => habilidadeLabels[id] || id).join(', ') || 'não vinculada';
  const recent = trace.evidence.items[0];
  const evidence = recent ? `${formatDate(recent.date)} — ${recent.activity} — ${recent.result}` : 'sem evidência relacionada';
  return `Barreira (${trace.barrierSource}): ${trace.barrier || '-'} | Habilidade: ${skill} | Meta: ${goal.descricao || '-'} | Evidência mais recente: ${evidence} | Próxima revisão: ${formatDate(trace.reviewDate)}`;
}
